import type { Charge, CreditCardInfo, Pix, PixRefund } from 'types/Charge';
import { toAmount } from './chargeSummary';

/**
 * Os dados do comprovante de estorno.
 *
 * Fora do componente porque é aqui que se decide o que o documento afirma — quanto voltou,
 * quando, e para quem. O componente só desenha; um campo nulo é uma linha que não aparece.
 */
export interface RefundReceiptData {
  method: 'PIX' | 'CREDIT_CARD';
  amount: number;
  refundedAt: string | null;
  payerName: string | null;
  payerDocument: string | null;
  originalAmount: number;
  paidAt: string | null;
  chargeId: string;
  sequence: string | null;
  description: string | null;
  /** Pix: o identificador da devolução no banco (rtrId). */
  refundId: string | null;
  endToEndId: string | null;
  txId: string | null;
  card: string | null;
  brand: string | null;
  installments: number | null;
  authorizationCode: string | null;
  nsu: string | null;
}

interface Payer {
  name: string | null;
  document: string | null;
}

const orNull = (value?: unknown): string | null => {
  if (value === null || value === undefined) return null;

  const normalized = String(value).trim();
  return normalized.length ? normalized : null;
};

const chargePerson = (charge: Charge): Payer => ({
  name: orNull(charge.person?.name),
  document: orNull(charge.person?.document),
});

const movementPayer = (charge: Charge): Payer => {
  const movement = (charge.Movements ?? []).find((item) => item?.payerName || item?.payerDocument);

  return {
    name: orNull(movement?.payerName),
    document: orNull(movement?.payerDocument),
  };
};

const hasPayer = (payer: Payer) => Boolean(payer.name || payer.document);

const chargeFields = (charge: Charge) => ({
  chargeId: charge.id,
  sequence: orNull(charge.sequence),
  description: orNull(charge.description),
});

export const buildPixRefundReceipt = (charge: Charge, pix: Pix, refund: PixRefund): RefundReceiptData => {
  /**
   * A devolução volta para a conta que pagou o Pix, e o devedor informado na cobrança pode ser
   * outra pessoa. Quem pagou de verdade está no movimento; o devedor é só o último recurso.
   */
  const paidBy = movementPayer(charge);
  const payer = hasPayer(paidBy) ? paidBy : chargePerson(charge);

  return {
    method: 'PIX',
    amount: toAmount(refund.amount),
    // `updatedAt` é a confirmação do banco; `createdAt` é só o pedido de devolução.
    refundedAt: orNull(refund.updatedAt) ?? orNull(refund.createdAt),
    payerName: payer.name,
    payerDocument: payer.document,
    originalAmount: toAmount(pix.amount),
    paidAt: orNull(charge.payedAt),
    ...chargeFields(charge),
    refundId: orNull(refund.endToEndId),
    endToEndId: orNull(pix.endToEndId),
    txId: orNull(pix.referenceIntegrationId),
    card: null,
    brand: null,
    installments: null,
    authorizationCode: null,
    nsu: null,
  };
};

export const buildCardRefundReceipt = (charge: Charge, creditCard: CreditCardInfo): RefundReceiptData => {
  const person = chargePerson(charge);
  const payer = hasPayer(person) ? person : movementPayer(charge);

  /**
   * O estorno de cartão não tem registro próprio: o valor fica acumulado na cobrança. Um
   * pagamento só aceita um estorno, então o acumulado é o valor deste. Estorno que não passou
   * por esse caminho deixa o acumulado em zero — e aí só pode ter sido o valor inteiro.
   */
  const refunded = toAmount(charge.amountRefunded);

  return {
    method: 'CREDIT_CARD',
    amount: refunded > 0 ? refunded : toAmount(creditCard.amount),
    refundedAt: orNull(creditCard.refundedAt),
    payerName: payer.name,
    payerDocument: payer.document,
    originalAmount: toAmount(creditCard.amount),
    paidAt: orNull(charge.payedAt) ?? orNull(creditCard.createdAt),
    ...chargeFields(charge),
    refundId: null,
    endToEndId: null,
    txId: null,
    card: orNull(creditCard.creditCard),
    brand: orNull(creditCard.brand),
    installments: creditCard.installments ?? null,
    authorizationCode: orNull(creditCard.authorizationCode),
    nsu: orNull(creditCard.nsu),
  };
};

/** Devolução em processamento ainda pode ser recusada pelo banco: não comprova nada. */
export const canIssuePixRefundReceipt = (refund: PixRefund): boolean => refund?.status === 'FINISHED';

export const canIssueCardRefundReceipt = (creditCard: CreditCardInfo): boolean => creditCard?.status === 'REFUNDED';

/** Um Pix aceita várias devoluções; o cartão, uma só por pagamento. */
export const refundReceiptFilename = (receipt: RefundReceiptData): string =>
  receipt.method === 'PIX'
    ? `comprovante-devolucao-${receipt.refundId ?? receipt.chargeId}.png`
    : `comprovante-estorno-${receipt.chargeId}.png`;
